import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [token, setToken] = useState('');
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [errorReason, setErrorReason] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    setToken(t);

    if (!t) {
      setVerifying(false);
      setValid(false);
      setErrorReason('missing');
      return;
    }

    (async () => {
      try {
        const resp = await axios.get(`${API}/auth/verify-reset-token/${t}`);
        if (resp.data?.valid) {
          setValid(true);
          setEmail(resp.data.email || '');
        } else {
          setValid(false);
          setErrorReason(resp.data?.reason || 'invalid');
        }
      } catch (err) {
        setValid(false);
        setErrorReason('error');
      } finally {
        setVerifying(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(resp.data?.message || 'Şifreniz güncellendi. Giriş sayfasına yönlendiriliyorsunuz...');
      setTimeout(() => {
        window.location.href = '/';
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Şifre güncellenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const reasonText = (reason) => {
    switch (reason) {
      case 'expired':
        return '⏰ Bu sıfırlama bağlantısının süresi dolmuş. Lütfen yeni bir bağlantı talep edin.';
      case 'invalid_or_used':
        return '🚫 Bu bağlantı geçersiz veya daha önce kullanılmış.';
      case 'missing':
        return '❌ Sıfırlama tokeni bulunamadı. Lütfen e-postadaki bağlantıyı kullanın.';
      default:
        return '⚠️ Geçersiz sıfırlama bağlantısı.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Card className="shadow-2xl border-slate-700 bg-slate-800/50 backdrop-blur" data-testid="reset-password-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-bold text-center text-white">
              🔐 Şifre Sıfırla
            </CardTitle>
            <p className="text-sm text-center text-slate-400">
              Yeni şifrenizi belirleyin
            </p>
          </CardHeader>
          <CardContent>
            {verifying && (
              <div className="text-center text-slate-300 py-6" data-testid="reset-verifying">
                ⏳ Bağlantı doğrulanıyor...
              </div>
            )}

            {!verifying && !valid && (
              <div className="space-y-4" data-testid="reset-invalid">
                <Alert variant="destructive" className="bg-red-900/40 border-red-700">
                  <AlertDescription className="text-red-200">
                    {reasonText(errorReason)}
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={() => (window.location.href = '/')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="reset-back-to-login"
                >
                  Giriş Sayfasına Dön
                </Button>
              </div>
            )}

            {!verifying && valid && !success && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
                {email && (
                  <div className="bg-slate-700/40 border border-slate-600 rounded-lg p-3 text-sm text-slate-300">
                    Hesap: <strong className="text-white">{email}</strong>
                  </div>
                )}
                <div>
                  <Label className="text-slate-300">Yeni Şifre</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                    data-testid="reset-new-password"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Yeni Şifre (Tekrar)</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-emerald-500"
                    data-testid="reset-confirm-password"
                  />
                </div>
                {error && (
                  <Alert variant="destructive" className="bg-red-900/40 border-red-700">
                    <AlertDescription className="text-red-200">{error}</AlertDescription>
                  </Alert>
                )}
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? '⏳ Kaydediliyor...' : '✅ Şifreyi Güncelle'}
                </Button>
              </form>
            )}

            {success && (
              <Alert className="bg-emerald-900/40 border-emerald-700" data-testid="reset-success">
                <AlertDescription className="text-emerald-200">{success}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
